import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { ContractsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/contracts/types/contracts.enum';
import { Wallet03 } from '@untitled-ui/icons-react';
import { Modal, notification } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useCreatePayment } from '@/features/contracts/api/use-create-payment';
import { useGetContractsSaldo } from '@/features/invoices/api/use-get-saldo';
import { formatAsCurrency } from '@/helpers/number-formatter';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

import { useGetContracts } from '../../contracts/api/use-get-contracts';

interface ContractPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContractPaymentModal = ({ isOpen, onClose }: ContractPaymentModalProps) => {
  const { t, i18n } = useTranslation('invoicesPage');

  const { selectedAccount } = useUserInfoStore();

  const [prePayments, setPrePayments] = React.useState<Record<number, number>>({});

  const contractsQuery = useGetContracts({
    params: {
      disablePagination: true,
      ownerCodes: [selectedAccount?.code],
      sideloading: [ContractsSideloading.ContractDebt],
    },
    queryConfig: {
      enabled: Boolean(selectedAccount?.code && isOpen),
    },
  });
  const contracts = contractsQuery.data as Contract[];

  const contractsSaldoQuery = useGetContractsSaldo({
    params: {
      contractNumbers: contracts?.map(({ contractNumber }) => contractNumber),
    },
    queryConfig: {
      enabled: Boolean(contracts?.length && isOpen),
    },
  });

  const createPaymentMutation = useCreatePayment({
    mutationConfig: {
      onSuccess: ({ data: { paymentLink } }) => {
        window.location.href = paymentLink;
      },
    },
  });

  const handlePayButtonClick = (contractId: number) => {
    const prepaymentAmount = prePayments[contractId] || 0;

    createPaymentMutation.mutate(
      { contractId, prepaymentAmount, locale: i18n.language },
      {
        onError: () => {
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      },
    );
  };

  const getSaldoAmount = (contractNumber: string) => {
    return contractsSaldoQuery.data?.data?.find((saldo) => saldo.code === `L${contractNumber}`)?.amount;
  };

  const handlePrePaymentInputChange = (contractId: number, value: string) => {
    const numeric = Number(value.replace(/[^\d.]/g, ''));
    setPrePayments((prev) => ({
      ...prev,
      [contractId]: isNaN(numeric) ? 0 : numeric,
    }));
  };

  return (
    <Modal
      centered
      open={isOpen}
      loading={contractsSaldoQuery.isPending && contractsQuery.isPending}
      width={920}
      onCancel={onClose}
      styles={{
        body: {
          overflowY: 'auto',
          maxHeight: '529px',
        },
        content: {
          borderRadius: 24,
        },
      }}
      title={<span className="text-[20px] block items-start text-center font-bold">{t('toPay')}</span>}
      footer={false}
    >
      <div className="grid grid-cols-4 text-gray-10 font-normal text-base">
        <div className="col-span-4 items-center grid grid-cols-4 h-[85px]">
          <div className="col-span-1 text-center">{t('contractNumber')}</div>
          <div className="col-span-1 text-center">{t('outstandingAmount')}</div>
          <div className="col-span-1 text-center">{t('prePayment')}</div>
        </div>
        {contracts?.map(({ contractNumber, id }) => {
          const saldo = getSaldoAmount(contractNumber);
          const prePaymentAmount = prePayments[id] || 0;
          const totalToPay = (saldo > 0 ? saldo : 0) + prePaymentAmount;

          return (
            <div key={contractNumber} className="col-span-4 gap-x-4 items-center grid grid-cols-4 h-[85px] border-t border-t-[#DFDFDF]">
              <div className="col-span-1 text-center font-normal">L{contractNumber}</div>
              <div className="col-span-1 text-center">
                {saldo > 0 ? (
                  <span>{formatAsCurrency(saldo)}</span>
                ) : (
                  <span className="text-[#279643] bg-[#E7FDDF] rounded-[99px] py-1 px-3">{t('paid')}</span>
                )}
              </div>
              <div className="col-span-1 text-center">
                <Input
                  parentClassName="h-12"
                  className="text-center w-full"
                  value={
                    prePayments[id] !== undefined && prePayments[id] !== 0 ? Intl.NumberFormat('et-ET').format(Number(prePayments[id])) : ''
                  }
                  onChange={(e) => handlePrePaymentInputChange(id, e.target.value)}
                  placeholder={t('prePayment')}
                />
              </div>
              <div className="col-span-1 text-center">
                <Button
                  pending={createPaymentMutation.isPending}
                  disabled={(saldo <= 0 && prePaymentAmount <= 0) || createPaymentMutation.isPending}
                  variant={saldo > 0 || prePaymentAmount > 0 ? 'primary' : 'secondaryAlt'}
                  className={cn(
                    'flex justify-center w-full h-12 max-h-12',
                    saldo > 0 || prePaymentAmount > 0 ? 'text-gray-10' : 'text-white',
                  )}
                  onClick={() => handlePayButtonClick(id)}
                >
                  <Wallet03 />
                  <span className="h-auto py-2 font-medium text-base text-ellipsis overflow-hidden">
                    {t('pay')} {formatAsCurrency(totalToPay)}
                  </span>
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ContractPaymentModal;
